import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Box,
  Button,
} from "@mui/material";
import { Search, AutoAwesome, Route as RouteIcon } from "@mui/icons-material";
import { DateFilter } from "@/components/ui";
import { useDeliveriesFilters } from "./useDeliveriesFilters";
import { deliveryService, type DeliveryStatus } from "@/services/delivery.service";
import { driverService } from "@/services/driver.service";
import { zoneService } from "@/services/zone.service";
import { useToastStore } from "@/store/toastStore";

/**
 * Bloc de filtres commun à tous les modes Livraisons (Liste, Par Zone, Par Livreur).
 * Synchronisé avec l’URL pour rester identique au changement de mode.
 */
export function DeliveriesFilters(): JSX.Element {
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToastStore();
  const { filters, setSearch, setStatus, setDriver, setZone, setDate } =
    useDeliveriesFilters();

  const allocateAutoMutation = useMutation({
    mutationFn: (deliveryDate: string) => deliveryService.allocateAuto(deliveryDate),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      const msg =
        data.created > 0 || data.assigned > 0 || data.tourOrdersSet > 0
          ? `Répartition automatique : ${data.created} créée(s), ${data.assigned} assignée(s), ${data.tourOrdersSet} ordre(s) défini(s).`
          : "Aucune commande sans livraison pour cette date.";
      showSuccess(msg);
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message ?? "Erreur lors de la répartition automatique.");
    },
  });

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => driverService.getAll(false),
  });

  const { data: zones = [] } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(),
  });

  const { data: deliveriesForDate = [] } = useQuery({
    queryKey: ["deliveries", "for-optimize", filters.date],
    queryFn: () => deliveryService.getAll({ deliveryDate: filters.date }),
    enabled: !!filters.date,
  });

  const driverIdsWithDeliveries = useMemo(() => {
    const ids = deliveriesForDate
      .map((d) => (d as { driverId?: string }).driverId)
      .filter(Boolean) as string[];
    return [...new Set(ids)];
  }, [deliveriesForDate]);

  const optimizeRouteMutation = useMutation({
    mutationFn: async ({
      deliveryDate,
      driverIds,
    }: { deliveryDate: string; driverIds: string[] }) => {
      const start = performance.now();
      for (const driverId of driverIds) {
        await deliveryService.optimizeRoute(driverId, deliveryDate);
      }
      const durationMs = Math.round(performance.now() - start);
      return { count: driverIds.length, durationMs };
    },
    onSuccess: ({ count, durationMs }) => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      const durationText =
        durationMs < 1000
          ? `${durationMs} ms`
          : durationMs < 60000
            ? `${(durationMs / 1000).toFixed(1).replace(".", ",")} s`
            : `${Math.floor(durationMs / 60000)} min ${Math.round((durationMs % 60000) / 1000)} s`;
      showSuccess(
        count > 0
          ? `Itinéraires optimisés pour ${count} livreur(s) en ${durationText} (Google Maps).`
          : "Aucun livreur avec livraisons à optimiser.",
      );
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message ?? "Impossible d'optimiser l'itinéraire (géocodage ou service OSRM).");
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <TextField
          placeholder="Rechercher une livraison, commande, client..."
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={filters.status}
            onChange={(e) => setStatus(e.target.value as DeliveryStatus | "all")}
            label="Statut"
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="PLANNED">Planifiée</MenuItem>
            <MenuItem value="IN_PROGRESS">En cours</MenuItem>
            <MenuItem value="DELIVERED">Livrée</MenuItem>
            <MenuItem value="FAILED">Échouée</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Livreur</InputLabel>
          <Select
            value={filters.driver}
            onChange={(e) => setDriver(e.target.value)}
            label="Livreur"
          >
            <MenuItem value="all">Tous</MenuItem>
            {drivers.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.firstName} {d.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Zone</InputLabel>
          <Select
            value={filters.zone}
            onChange={(e) => setZone(e.target.value)}
            label="Zone"
          >
            <MenuItem value="all">Toutes</MenuItem>
            {zones.map((z) => (
              <MenuItem key={z.id} value={z.id}>
                {z.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateFilter
          value={filters.date}
          onChange={setDate}
          label="Date de livraison"
          showNavArrows
          showTodayButton
          size="small"
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AutoAwesome />}
          onClick={() => allocateAutoMutation.mutate(filters.date)}
          disabled={allocateAutoMutation.isPending || !filters.date}
        >
          {allocateAutoMutation.isPending ? "Répartition…" : "Répartition automatique"}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RouteIcon />}
          onClick={() =>
            optimizeRouteMutation.mutate({
              deliveryDate: filters.date,
              driverIds: driverIdsWithDeliveries,
            })
          }
          disabled={
            optimizeRouteMutation.isPending ||
            !filters.date ||
            driverIdsWithDeliveries.length === 0
          }
          sx={{ borderStyle: "dashed", color: "primary.main" }}
        >
          {optimizeRouteMutation.isPending
            ? "Optimisation…"
            : "Optimiser l'itinéraire"}
        </Button>
    </Box>
  );
}
